/**
 * nameHelpers.js — Shared name formatting utilities.
 * Used by MiniKvantCard, DepartmentDrawer, EmployeeDrawer, MapView.
 */

/**
 * Get initials from full name (max 2 characters).
 * "Иванов Петр Сергеевич" → "ИП"
 */
export function getInitials(fullName) {
    if (!fullName) return '?'
    return fullName.split(' ').slice(0, 2).map(w => w[0]).join('').toUpperCase()
}

/**
 * Как displayName, но с девичьей фамилией в скобках.
 * "Засыпкина (Турсунова) Ксения Андреевна" → "Засыпкина (Турсунова) Ксения".
 * Нужна там, где человека ищут глазами по обеим фамилиям — Личное дело и
 * оргструктура (Наталья, 2026-07-26). В списках, отчётах и уведомлениях —
 * обычный displayName, без скобок.
 */
export function displayNameWithMaiden(fullName) {
    if (!fullName) return ''
    const out = []
    for (const word of String(fullName).trim().split(/\s+/)) {
        if (word.startsWith('(')) {
            out.push(word)  // девичья фамилия не занимает место фамилии или имени
            continue
        }
        if (out.filter(w => !w.startsWith('(')).length >= 2) break
        out.push(word)
    }
    return out.join(' ')
}

/**
 * Компактная форма с девичьей фамилией: «Засыпкина (Турсунова) К.».
 * Для сводных карточек оргструктуры, где полное имя не помещается, но обе
 * фамилии человека видеть нужно.
 */
export function shortNameWithMaiden(fullName) {
    const parts = displayNameWithMaiden(fullName).split(' ')
    const last = parts[parts.length - 1]
    if (parts.length >= 2 && last) return [...parts.slice(0, -1), last[0] + '.'].join(' ')
    return parts[0] || ''
}

/**
 * Shorten full name for compact display (узкие карточки, чипы, колонки таблиц).
 * "Иванов Пётр Сергеевич" → "Иванов П."  — отчество не показываем даже инициалом.
 * "Иванов Пётр" → "Иванов П."
 */
export function shortName(fullName) {
    const parts = displayName(fullName).split(' ')
    if (parts.length >= 2) return `${parts[0]} ${parts[1][0]}.`
    return parts[0] || ''
}

/**
 * Display name WITHOUT patronymic — Фамилия + Имя only.
 * "Иванов Пётр Сергеевич" → "Иванов Пётр". "Иванов Пётр" → "Иванов Пётр".
 * Используется во ВСЕХ модулях для отображения ФИО; полное ФИО (с отчеством)
 * оставляем ТОЛЬКО в Личном деле сотрудника (personnelFile/*).
 */
export function displayName(fullName) {
    if (!fullName) return ''
    // «Засыпкина (Турсунова) Ксения Андреевна» → «Засыпкина Ксения»: без выброса
    // скобок в отображение попала бы одна фамилия. Бэкенд-близнец — app/utils/names.py.
    return String(fullName).replace(/\([^)]*\)/g, ' ').trim().split(/\s+/).slice(0, 2).join(' ')
}
